import { useEffect, useMemo, useRef, useState } from "react";
import { InputBox, Selectbox, CheckboxComponent } from "../../../../../FormComponent";
import SmartInputBox from "../../../shared/SmartInputBox";
import styles from "../../../../../../styles/automationStudio/automationStudio.module.scss";
import {
    SKIP_ARGUMENTS,
    ONLY_CUSTOM_ARGUMENTS,
    toolSupportsElements,
} from '../../../../../AutomationStudio/constant';

/** Normalize argument value to string for SmartInputBox (e.g. array -> comma-separated). */
function toDisplayValue(val) {
    if (val == null) return '';
    if (Array.isArray(val)) return val.map((v) => (v != null ? String(v) : '')).join(', ');
    return String(val);
}

/**
 * Comma-separated array input. Keeps the raw text in local state so trailing commas/
 * spaces aren't clobbered while the user is mid-typing. Pushes the parsed string[]
 * to the parent on every keystroke; resyncs from the prop only when the external
 * value actually changes (tool switch, undo).
 */
function ArrayCommaInput({
    value,
    onChange,
    placeholder,
    disabled,
    suggestions,
    descriptions,
    fileSuggestions,
    fileDescriptions,
    error = '',
}) {
    const [rawText, setRawText] = useState(() => toDisplayValue(value));
    const lastExternalRef = useRef(value);

    useEffect(() => {
        if (JSON.stringify(lastExternalRef.current) !== JSON.stringify(value)) {
            setRawText(toDisplayValue(value));
            lastExternalRef.current = value;
        }
    }, [value]);

    const handleChange = (e) => {
        const raw = e.target.value;
        setRawText(raw);
        const parts = raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
        lastExternalRef.current = parts;
        onChange(parts);
    };

    return (
        <SmartInputBox
            size="small"
            value={rawText}
            onChange={handleChange}
            placeholder={placeholder}
            suggestions={suggestions}
            descriptions={descriptions}
            fileTrigger="@"
            fileSuggestions={fileSuggestions}
            fileDescriptions={fileDescriptions}
            formatFileInsertion={(file) => `@[${file.id}]`}
            disabled={disabled}
            error={error}
        />
    );
}

/**
 * ArgumentsTab — same key resolution as Replay ScriptDetail `ArgumentsComponent`:
 * iterate step args (filtered); per key use `mcpToolMap[tool][key]` when present, else infer from value.
 */
export default function ArgumentsTab({
    step,
    onStepChange,
    isExecuting = false,
    readOnlyForMetaInfo = false,
    mcpToolMap = {},
    fileSuggestions = [],
    fileDescriptions = {},
    variablesList = [],
    variablesListDescriptions = {},
    argsFieldDirty = null,
}) {
    const suggestions = fileSuggestions;
    const effectiveArgsFieldDirty = step?.customStep ? null : argsFieldDirty;

    const args = step.args ?? step.arguments ?? {};
    const toolSchema = step.tool && mcpToolMap[step.tool] ? mcpToolMap[step.tool] : null;

    const isSystemManaged = step?.tool === 'browser_network_requests';
    const editorLocked = isExecuting || readOnlyForMetaInfo || isSystemManaged;

    const supportsElements = toolSupportsElements(step.tool);

    const visibleKeys = useMemo(
        () =>
            Object.keys(args).filter((k) => {
                if (SKIP_ARGUMENTS.includes(k)) return false;
                if (ONLY_CUSTOM_ARGUMENTS.includes(k)) {
                    if (!step.customStep || !supportsElements) return false;
                }
                return true;
            }),
        [args, step.customStep, supportsElements]
    );

    const handleArgumentChange = (key, value) => {
        if (isSystemManaged) return;  // belt-and-suspenders: inputs are disabled, but guard writes too
        if (editorLocked) return;
        const updatedArgs = { ...args, [key]: value };
        const updates = { args: updatedArgs };

        if (
            supportsElements &&
            step.customStep &&
            (key === 'selector' || key === 'startSelector' || key === 'endSelector')
        ) {
            const newStrategies = [];
            if (step.tool === 'browser_drag') {
                const start = key === 'startSelector' ? value : updatedArgs.startSelector;
                const end = key === 'endSelector' ? value : updatedArgs.endSelector;
                if (start?.trim() && end?.trim()) {
                    newStrategies.push({
                        id: 1, type: 'xpath_custom', enabled: true, priority: 1,
                        startSelector: start, endSelector: end, selectorType: 'xpath',
                        description: 'User-provided XPath selectors for drag operation',
                        confidence: 'medium', createdBy: 'user',
                        createdAt: new Date().toISOString(),
                    });
                }
            } else {
                const sel = key === 'selector' ? value : updatedArgs.selector;
                if (sel?.trim()) {
                    newStrategies.push({
                        id: 1, type: 'xpath_custom', enabled: true, priority: 1,
                        selector: sel, selectorType: 'xpath',
                        description: 'User-provided XPath selector',
                        confidence: 'medium', createdBy: 'user',
                        createdAt: new Date().toISOString(),
                    });
                }
            }
            updates.executionStrategies = newStrategies;
        }

        onStepChange(updates);
    };

    const getArgType = (value, schemaType) => {
        if (schemaType === 'boolean') return 'boolean';
        if (typeof value === 'boolean') return 'boolean';
        if (typeof value === 'number') return 'number';
        if (schemaType === 'number') return 'number';
        if (schemaType === 'array' || Array.isArray(value)) return 'array';
        if (typeof value === 'object' && value !== null) return 'json';
        return 'text';
    };

    const formatArgLabel = (key) => {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    const getFieldError = (key) => (
        step.argsError?.includes(key) ? `${formatArgLabel(key)} is required` : ''
    );

    const renderArgumentControl = (key, schema, value, isFieldDirty = false) => {
        const dirtyWrap = isFieldDirty ? styles.dirtyFieldTarget : undefined;
        const withDirtyWrap = (node) => (dirtyWrap ? <div className={dirtyWrap}>{node}</div> : node);
        const schemaType = schema?.type || 'string';
        const argType = getArgType(value, schemaType);
        const fieldId = `arg-${step?.id ?? 'step'}-${key}`;
        const fieldError = getFieldError(key);

        const isDisabled = isExecuting || isSystemManaged || editorLocked;

        if (argType === 'boolean') {
            return (
                <CheckboxComponent
                    id={fieldId}
                    size="small"
                    className={styles.argumentsCheckbox}
                    checked={value === true || value === 'true'}
                    onChange={(e) => handleArgumentChange(key, e.target.checked)}
                    disabled={isDisabled}
                    aria-labelledby={`${fieldId}-caption`}
                    aria-invalid={fieldError ? 'true' : undefined}
                />
            );
        }

        if (schema?.enum?.length) {
            return withDirtyWrap(
                <Selectbox
                    id={fieldId}
                    size="small"
                    value={value != null ? String(value) : ''}
                    onChange={(e) => handleArgumentChange(key, e.target.value)}
                    options={schema.enum.map((item) => ({ value: item, label: item }))}
                    placeholder={schema?.description}
                    disabled={isDisabled}
                    error={fieldError}
                    aria-labelledby={`${fieldId}-caption`}
                />
            );
        }

        if (argType === 'array') {
            // Comma-separated input. Stored as string[]; commas inside individual values are not supported.
            return withDirtyWrap(
                <ArrayCommaInput
                    value={value}
                    onChange={(arr) => handleArgumentChange(key, arr)}
                    placeholder={`${schema?.description ?? `Values for ${formatArgLabel(key)}`} — comma-separated, e.g. Option 1, Option 2`}
                    disabled={isDisabled}
                    suggestions={variablesList}
                    descriptions={variablesListDescriptions}
                    fileSuggestions={suggestions}
                    fileDescriptions={fileDescriptions}
                    error={fieldError}
                />
            );
        }

        if (argType === 'number' || schemaType === 'number') {
            return withDirtyWrap(
                <InputBox
                    id={fieldId}
                    size="small"
                    type="number"
                    value={value != null ? value : ''}
                    onChange={(e) => handleArgumentChange(key, e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder={schema?.description}
                    fullWidth
                    disabled={isDisabled}
                    error={fieldError}
                    aria-labelledby={`${fieldId}-caption`}
                />
            );
        }

        if (!schema && argType === 'json') {
            return withDirtyWrap(
                <InputBox
                    id={fieldId}
                    size="small"
                    type="text"
                    value={JSON.stringify(value, null, 2)}
                    onChange={(e) => {
                        try {
                            const parsed = JSON.parse(e.target.value);
                            handleArgumentChange(key, parsed);
                        } catch (err) {}
                    }}
                    placeholder={`JSON value for ${key}`}
                    disabled={isDisabled}
                    error={fieldError}
                    aria-labelledby={`${fieldId}-caption`}
                />
            );
        }

        return withDirtyWrap(
            <SmartInputBox
                size="small"
                value={toDisplayValue(value)}
                onChange={(e) => handleArgumentChange(key, e.target.value)}
                placeholder={schema?.description ?? `Enter ${formatArgLabel(key)}`}
                suggestions={variablesList}
                descriptions={variablesListDescriptions}
                fileTrigger="@"
                fileSuggestions={suggestions}
                fileDescriptions={fileDescriptions}
                formatFileInsertion={(file) => `@[${file.id}]`}
                disabled={isDisabled}
                error={fieldError}
            />
        );
    };

    return (
        <div className={styles.argumentsTabContainer}>
            {visibleKeys.length === 0 ? (
                <div className={styles.emptyState}>
                    <p className={`${styles.asTypoBody2} ${styles.asTextSecondary}`}>
                        {toolSchema && Object.keys(toolSchema).length === 0
                            ? 'No arguments for this tool'
                            : 'No arguments defined for this step'}
                    </p>
                    <p className={`${styles.asTypoCaption} ${styles.asTextSecondary}`}>
                        {toolSchema && Object.keys(toolSchema).length === 0
                            ? 'Schema has no properties.'
                            : 'No custom arguments for this step'}
                    </p>
                </div>
            ) : (
                <div className={styles.formSection}>
                    {visibleKeys.map((key) => {
                        const schema = toolSchema?.[key];
                        const value = args[key];
                        const schemaType = schema?.type || 'string';
                        const argType = getArgType(value, schemaType);
                        const requiredMark =
                            (
                                schema?.required ||
                                (step.customStep && supportsElements && ONLY_CUSTOM_ARGUMENTS.includes(key))
                            ) ? ' *' : '';
                        const labelText = `${formatArgLabel(key)}:${requiredMark}`;
                        const fieldId = `arg-${step?.id ?? 'step'}-${key}`;
                        const isMultilineJson = !schema && argType === 'json';
                        const fieldError = getFieldError(key);

                        return (
                            <div
                                key={key}
                                className={`${styles.argumentsFieldRow} ${isMultilineJson ? styles.argumentsFieldRowMultiline : ''} ${fieldError ? styles.argumentsFieldRowError : ''}`}
                            >
                                <span className={styles.argumentsFieldLabel} id={`${fieldId}-caption`}>
                                    {labelText}
                                </span>
                                <div className={styles.argumentsFieldControlMain}>
                                    {renderArgumentControl(key, schema, value, Boolean(effectiveArgsFieldDirty?.[key]))}
                                </div>
                                {fieldError && argType === 'boolean' && (
                                    <p
                                        className={`${styles.argumentsFieldError} ${styles.asFormHelperText} ${styles.errorText}`}
                                        role="alert"
                                    >
                                        {fieldError}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
